import { v2Api } from './v2Client';

const LOG = '[SquashAPI terrainPricing]';

/**
 * Bits lundi→dimanche choisis côté front (le plan ne fixe pas l'ordre exact du bitmask
 * `DaysOfWeek` — à réconcilier avec le backend si les valeurs stockées diffèrent, voir note de
 * livraison). Un jour peut être couvert par plusieurs bits à la fois (ex. lun-ven).
 */
export const DAY_OF_WEEK_BITS = [
  { bit: 1, short: 'Lun', label: 'Lundi' },
  { bit: 2, short: 'Mar', label: 'Mardi' },
  { bit: 4, short: 'Mer', label: 'Mercredi' },
  { bit: 8, short: 'Jeu', label: 'Jeudi' },
  { bit: 16, short: 'Ven', label: 'Vendredi' },
  { bit: 32, short: 'Sam', label: 'Samedi' },
  { bit: 64, short: 'Dim', label: 'Dimanche' },
];

export function dateToDayBit(date) {
  const jsDay = date.getDay(); // 0=dimanche .. 6=samedi
  const idx = jsDay === 0 ? 6 : jsDay - 1; // 0=lundi .. 6=dimanche
  return DAY_OF_WEEK_BITS[idx].bit;
}

export function daysOfWeekBitmaskLabel(mask) {
  const n = Number(mask) || 0;
  const shorts = DAY_OF_WEEK_BITS.filter((d) => (n & d.bit) !== 0).map((d) => d.short);
  return shorts.length ? shorts.join(' ') : '—';
}

function normalizeRule(row) {
  if (!row) return null;
  const id = row.id ?? row.Id;
  if (id == null) return null;
  return {
    id: String(id),
    sport: row.sport ?? row.Sport ?? 'squash',
    terrain_id: row.terrain_id ?? row.terrainId ?? row.TerrainId ?? null,
    days_of_week: Number(row.days_of_week ?? row.daysOfWeek ?? row.DaysOfWeek ?? 0) || 0,
    start_time: row.start_time ?? row.startTime ?? row.StartTime ?? '00:00:00',
    end_time: row.end_time ?? row.endTime ?? row.EndTime ?? '00:00:00',
    price_cents_per_hour: Number(row.price_cents_per_hour ?? row.priceCentsPerHour ?? row.PriceCentsPerHour ?? 0) || 0,
    valid_from: row.valid_from ?? row.validFrom ?? row.ValidFrom ?? null,
    valid_to: row.valid_to ?? row.validTo ?? row.ValidTo ?? null,
    label: row.label ?? row.Label ?? '',
    color_hex: row.color_hex ?? row.colorHex ?? row.ColorHex ?? null,
    membership_plan_id: row.membership_plan_id ?? row.membershipPlanId ?? row.MembershipPlanId ?? null,
    membership_plan_name: row.membership_plan_name ?? row.membershipPlanName ?? null,
    match_category_id: row.match_category_id ?? row.matchCategoryId ?? null,
    match_category_name: row.match_category_name ?? row.matchCategoryName ?? null,
    row_version: row.row_version ?? row.rowVersion ?? row.RowVersion ?? null,
  };
}

/** Palette de couleurs par défaut proposée dans le sélecteur de règle de tarif (purement cosmétique). */
export const DEFAULT_PRICING_COLORS = [
  '#FDE68A', // jaune (heures creuses)
  '#FCA5A5', // rouge clair (heures pleines)
  '#93C5FD', // bleu
  '#86EFAC', // vert
  '#C4B5FD', // violet
  '#FDBA74', // orange
  '#F9A8D4', // rose
  '#D1D5DB', // gris neutre
];

/** GET /TerrainPricing/pricing_rules */
export async function listPricingRules() {
  try {
    const { data } = await v2Api.get('/TerrainPricing/pricing_rules');
    const rows = Array.isArray(data) ? data : [];
    return rows.map(normalizeRule).filter(Boolean);
  } catch (err) {
    console.error(LOG, 'pricing_rules échoué', err);
    throw err;
  }
}

/** POST /TerrainPricing/create */
export async function createPricingRule(payload) {
  try {
    const { data } = await v2Api.post('/TerrainPricing/create', payload);
    return normalizeRule(data) ?? data;
  } catch (err) {
    console.error(LOG, 'create échoué', { payload, err });
    throw err;
  }
}

/** POST /TerrainPricing/update */
export async function updatePricingRule(payload) {
  try {
    const { data } = await v2Api.post('/TerrainPricing/update', payload);
    return normalizeRule(data) ?? data;
  } catch (err) {
    console.error(LOG, 'update échoué', { payload, err });
    throw err;
  }
}

/** POST /TerrainPricing/delete — hard delete accepté ici (config pure, pas d'historique attaché). */
export async function deletePricingRule(id) {
  try {
    const { data } = await v2Api.post('/TerrainPricing/delete', { id: String(id) });
    return data;
  } catch (err) {
    console.error(LOG, 'delete échoué', { id, err });
    throw err;
  }
}

function timeStrToMinutes(t) {
  const s = String(t ?? '').trim();
  const m = /^(\d{1,2}):(\d{2})/.exec(s);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function dateOnly(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function ruleAppliesOnDate(rule, dayStart) {
  if (rule.valid_from) {
    const from = dateOnly(new Date(rule.valid_from));
    if (dayStart < from) return false;
  }
  if (rule.valid_to) {
    const to = dateOnly(new Date(rule.valid_to));
    if (dayStart > to) return false;
  }
  return true;
}

/**
 * Calcule, pour un jour/terrain/sport donné, les bandes de couleur de tarif à peindre en fond de la
 * grille de planning (purement cosmétique — n'a aucun rôle dans le calcul du prix réel, qui reste fait
 * côté serveur au moment de la création de la réservation). Découpe [startHour, endHour) par règle
 * applicable ce jour-là, même précédence que `computeReservationPriceCents` (terrain-spécifique >
 * générique du sport). Une règle sans `color_hex` renseigné ne produit aucune bande (fond neutre par
 * défaut, laissé à la grille).
 * @returns {{startAt: Date, endAt: Date, colorHex: string, label: string|null, ruleId: string}[]}
 */
export function computeTariffBandsForDay(rules, { sport, terrainId, date, startHour, endHour }) {
  const midnight = dateOnly(date instanceof Date ? date : new Date(date));
  const dayBit = dateToDayBit(midnight);
  const startMin = startHour * 60;
  const endMin = endHour * 60;

  const applicable = (Array.isArray(rules) ? rules : []).filter(
    (r) =>
      r.sport === sport &&
      (Number(r.days_of_week) & dayBit) !== 0 &&
      ruleAppliesOnDate(r, midnight) &&
      // Tarif « spécial » (formule d'abonnement ou type de réservation) : dépend du joueur ou de la réservation,
      // jamais peint sur la grille (la réservation prend la couleur de son type).
      r.membership_plan_id == null &&
      r.match_category_id == null &&
      (r.terrain_id == null || String(r.terrain_id) === String(terrainId))
  );
  if (!applicable.length) return [];

  const cutpoints = new Set([startMin, endMin]);
  for (const r of applicable) {
    const rs = timeStrToMinutes(r.start_time);
    const re = timeStrToMinutes(r.end_time);
    if (rs != null && rs > startMin && rs < endMin) cutpoints.add(rs);
    if (re != null && re > startMin && re < endMin) cutpoints.add(re);
  }
  const sorted = Array.from(cutpoints).sort((a, b) => a - b);

  const bands = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const subStart = sorted[i];
    const subEnd = sorted[i + 1];
    if (subEnd <= subStart) continue;
    const mid = (subStart + subEnd) / 2;
    const covering = applicable.filter((r) => {
      const rs = timeStrToMinutes(r.start_time);
      const re = timeStrToMinutes(r.end_time);
      return rs != null && re != null && mid >= rs && mid < re;
    });
    if (!covering.length) continue;
    const specific = covering.filter((r) => r.terrain_id != null);
    const winner = (specific.length ? specific : covering)[0];
    if (!winner.color_hex) continue;
    bands.push({
      startAt: new Date(midnight.getTime() + subStart * 60000),
      endAt: new Date(midnight.getTime() + subEnd * 60000),
      colorHex: winner.color_hex,
      label: winner.label || null,
      ruleId: winner.id,
    });
  }
  return bands;
}

/**
 * Calcule le prix (en centimes) d'un créneau [startAt, endAt) pour un terrain/sport donné, en
 * proratisant par sous-segment quand plusieurs règles tarifaires se chevauchent dans le temps
 * (plan §"Une réservation chevauche deux tranches tarifaires différentes"). Règle terrain-spécifique
 * > règle générique du même sport sur le même jour/heure. Retourne `null` si un sous-segment n'est
 * couvert par aucune règle (pas de prix inventé à 0€, conformément au plan).
 *
 * Reproduit côté front l'algorithme documenté pour `TerrainPricingService.ComputePriceCents` — le
 * plan Backend ne prévoit pas d'endpoint de prévisualisation dédié (seul `TerrainReservation/create`
 * calcule et fige le prix côté serveur), donc ce calcul tourne en local à partir des règles déjà
 * chargées (`listPricingRules`), pour alimenter à la fois le simulateur de l'onglet Tarifs et
 * l'aperçu de prix du formulaire de réservation. Voir note de livraison.
 */
export function computeReservationPriceCents(rules, { sport, terrainId, startAt, endAt }) {
  const start = startAt instanceof Date ? startAt : new Date(startAt);
  const end = endAt instanceof Date ? endAt : new Date(endAt);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) return null;

  const allRules = Array.isArray(rules) ? rules : [];
  const segments = [];
  let cursor = new Date(start);
  while (cursor < end) {
    const dayStart = dateOnly(cursor);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    const segEnd = end < dayEnd ? end : dayEnd;
    segments.push({ start: new Date(cursor), end: new Date(segEnd), dayStart });
    cursor = segEnd;
  }

  let totalCents = 0;
  let anyUncovered = false;

  for (const seg of segments) {
    const dayBit = dateToDayBit(seg.dayStart);
    const applicable = allRules.filter(
      (r) =>
        r.sport === sport &&
        (r.days_of_week & dayBit) !== 0 &&
        ruleAppliesOnDate(r, seg.dayStart) &&
        // Aperçu = tarif public ; le tarif membre est appliqué par l'API selon le joueur responsable.
        r.membership_plan_id == null &&
        r.match_category_id == null &&
        (r.terrain_id == null || String(r.terrain_id) === String(terrainId))
    );
    if (!applicable.length) {
      anyUncovered = true;
      continue;
    }

    const segStartMin = Math.round((seg.start.getTime() - seg.dayStart.getTime()) / 60000);
    const segEndMin = Math.round((seg.end.getTime() - seg.dayStart.getTime()) / 60000);

    const cutpoints = new Set([segStartMin, segEndMin]);
    for (const r of applicable) {
      const rs = timeStrToMinutes(r.start_time);
      const re = timeStrToMinutes(r.end_time);
      if (rs != null && rs > segStartMin && rs < segEndMin) cutpoints.add(rs);
      if (re != null && re > segStartMin && re < segEndMin) cutpoints.add(re);
    }
    const sorted = Array.from(cutpoints).sort((a, b) => a - b);

    for (let i = 0; i < sorted.length - 1; i++) {
      const subStart = sorted[i];
      const subEnd = sorted[i + 1];
      if (subEnd <= subStart) continue;
      const mid = (subStart + subEnd) / 2;
      const covering = applicable.filter((r) => {
        const rs = timeStrToMinutes(r.start_time);
        const re = timeStrToMinutes(r.end_time);
        return rs != null && re != null && mid >= rs && mid < re;
      });
      if (!covering.length) {
        anyUncovered = true;
        continue;
      }
      const specific = covering.filter((r) => r.terrain_id != null);
      const winner = (specific.length ? specific : covering)[0];
      const hours = (subEnd - subStart) / 60;
      totalCents += winner.price_cents_per_hour * hours;
    }
  }

  if (anyUncovered) return null;
  return Math.round(totalCents);
}

export const terrainPricingService = {
  list: listPricingRules,
  create: createPricingRule,
  update: updatePricingRule,
  remove: deletePricingRule,
  computePriceCents: computeReservationPriceCents,
  tariffBandsForDay: computeTariffBandsForDay,
};
